import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { UiModule } from 'src/app/ui/ui.module';
import { PipeModule } from 'src/app/pipes/pipes.module';
import { MatDialog } from '@angular/material/dialog';
import { SelectSpaceDialogComponent } from 'src/app/dialogs/select-space-dialog/select-space-dialog.component';
import { BehaviorSubject, take } from 'rxjs';
import { TooltipDirective } from '../../directives/pncl-tooltip.directive';
import { PencilButtonComponent } from '../pencil-button/pencil-button.component';
import { IconTypes } from '../pencil-icon/pencil-icon.component';
import { PencilPillComponent } from '../pencil-pill/pencil-pill.component';

export interface MultiSelectTagItem {
  label: string;

  [key: string]: unknown;
}

/**
 * Component for searching in a modal.
 * @template T - The type of the items in the addableList and selectedList arrays.
 */
@Component({
  selector: 'app-pencil-multi-select-modal',
  templateUrl: './pencil-multi-select-modal.component.html',
  styleUrls: ['./pencil-multi-select-modal.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    PencilPillComponent,
    TranslateModule,
    PencilButtonComponent,
    CommonModule,
    FormsModule,
    TooltipDirective,
    UiModule,
    PipeModule,
  ],
})
export class PencilMultiSelectModalComponent<T extends { [key: string]: any }>
  implements OnInit, OnChanges
{
  @Input() selectedList: T[] = [];
  @Input() maxShownTags: number = 4;
  @Input() placeholder = this.translate.instant('Add');
  @Input() keyPropName = 'key';
  @Input() valuePropName = 'title';
  @Input() header?: string;
  @Input() canEdit = false;
  @Input() canExpandHiddenTags = true;

  @Output() selectedItemsChange = new EventEmitter<T[]>();
  @Output() editOptionClicked = new EventEmitter<void>();

  isSelectedListEmpty$ = new BehaviorSubject<boolean>(true);
  displayedSelectedList$ = new BehaviorSubject<T[]>([]);
  hiddenSelectedList$ = new BehaviorSubject<T[]>([]);
  moreTagsToShow$ = new BehaviorSubject<boolean>(false);
  protected IconTypes = IconTypes;

  constructor(private translate: TranslateService, private dialog: MatDialog) {}

  ngOnInit(): void {
    this.updateLists(this.selectedList);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.selectedList && !changes.selectedList.firstChange) {
      this.updateLists(this.selectedList);
    }
  }

  openDialog(): void {
    const dialogRef = this.dialog.open(SelectSpaceDialogComponent, {
      panelClass: 'select-entity-dialog',
      autoFocus: false,
      restoreFocus: false,
      disableClose: true,
      data: { hideFooter: true, showAllSpacesEvenIfNotHost: true },
    });

    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe((result) => {
        if (result) {
          this.addSelectedItem(result);
        }
      });
  }

  private updateLists(selectedList: T[]): void {
    this.isSelectedListEmpty$.next(selectedList.length === 0);

    if (this.canEdit) {
      this.displayedSelectedList$.next(selectedList);
      this.hiddenSelectedList$.next([]);
      this.moreTagsToShow$.next(false);
    } else {
      this.displayedSelectedList$.next(selectedList.slice(0, this.maxShownTags));
      this.hiddenSelectedList$.next(selectedList.slice(this.maxShownTags));
      this.moreTagsToShow$.next(selectedList.length > this.maxShownTags);
    }
  }

  addSelectedItem(selectedItem: T): void {
    const exists = this.selectedList.some(
      (item) => item[this.keyPropName] === selectedItem[this.keyPropName],
    );

    if (!exists) {
      this.selectedList = [...this.selectedList, selectedItem];
      this.selectedItemsChange.emit(this.selectedList);
      this.isSelectedListEmpty$.next(this.selectedList.length === 0);
    }
  }

  removeSelectedItem(selectedItem: T): void {
    this.selectedList = this.selectedList.filter(
      (item) => item[this.keyPropName] !== selectedItem[this.keyPropName],
    );
    this.selectedItemsChange.emit(this.selectedList);
    this.isSelectedListEmpty$.next(this.selectedList.length === 0);
  }
}
